import { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Eyebrow from '../components/common/Eyebrow';
import { FAQShimmer } from '../components/shimmer';
import {
  useListFaqsQuery,
  useGetContactInfoQuery,
} from '../store/siteContentApi';

const FALLBACK_WA = '+919014638633';
const waHref = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '#';
};

/* ═══════════════════════════════════════════════════════════════
   FAQ PAGE — matches the HTML reference exactly
   ═══════════════════════════════════════════════════════════════ */
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const { data: faqs = [], isLoading } = useListFaqsQuery();
  const { data: contact } = useGetContactInfoQuery();

  const toggle = useCallback(
    (i) => setOpenIndex((prev) => (prev === i ? null : i)),
    [],
  );

  return (
    <Box>
      {/* ── Hero ── */}
      <Box
        sx={{
          bgcolor: 'var(--forest)',
          pt: { xs: 10, md: 12 },
          pb: { xs: 5, md: 10 },
          px: { xs: 2.5, md: 7 },
        }}
      >
        <Box sx={{ maxWidth: 1300, mx: 'auto' }}>
          <Eyebrow white>FAQ</Eyebrow>
          <Typography
            className="serif"
            variant="h1"
            sx={{ color: '#fff', mt: 1.5, mb: 1.5 }}
          >
            Common Questions
          </Typography>
          <Typography
            sx={{
              fontSize: 16,
              color: 'rgba(255,255,255,0.4)',
              fontWeight: 300,
            }}
          >
            Everything you need to know about TrustLab, Trustians, and your
            reports.
          </Typography>
        </Box>
      </Box>

      {/* ── FAQ Accordion ── */}
      <Box
        sx={{
          bgcolor: 'var(--cream)',
          py: { xs: 5, md: 8 },
          px: { xs: 2.5, md: 7 },
        }}
      >
        <Container maxWidth="sm" disableGutters sx={{ maxWidth: 800 }}>
          {isLoading && faqs.length === 0 && <FAQShimmer count={6} />}

          {faqs.map((f, i) => {
            const question = f.question;
            const answer = f.answer;
            const isOpen = openIndex === i;
            return (
              <Box
                key={f._id || i}
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '16px',
                  mb: 1.25,
                  border: `1.5px solid ${isOpen ? 'var(--sage)' : 'var(--border-l)'}`,
                  overflow: 'hidden',
                  transition: 'all 0.25s',
                }}
              >
                {/* Question */}
                <Box
                  onClick={() => toggle(i)}
                  sx={{
                    px: 3.25,
                    py: 2.75,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: 'var(--ink)',
                      lineHeight: 1.4,
                      pr: 1.5,
                    }}
                  >
                    {question}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'var(--sage)',
                      fontSize: 22,
                      transition: 'transform 0.25s',
                      transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                      lineHeight: 1,
                      fontWeight: 300,
                      flexShrink: 0,
                      ml: 1.5,
                    }}
                  >
                    +
                  </Typography>
                </Box>

                {/* Answer — shown only when open */}
                {isOpen && (
                  <Box
                    sx={{
                      px: 3.25,
                      pb: 2.75,
                      pt: 0,
                      borderTop: '1px solid var(--border-l)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: 'var(--stone)',
                        lineHeight: 1.72,
                        fontWeight: 300,
                        pt: 2,
                      }}
                    >
                      {answer}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}

          {/* ── CTA Card ── */}
          <Box
            sx={{
              bgcolor: 'var(--sage)',
              borderRadius: '24px',
              p: 5,
              mt: 5,
              textAlign: 'center',
            }}
          >
            <Typography
              className="serif"
              sx={{
                fontSize: { xs: 22, md: 26 },
                fontWeight: 600,
                color: '#fff',
                mb: 1.25,
              }}
            >
              Still have questions?
            </Typography>
            <Typography
              sx={{
                fontSize: 15,
                color: 'rgba(255,255,255,0.65)',
                mb: 3,
                fontWeight: 300,
                maxWidth: 420,
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              Our team responds on WhatsApp in under 5 minutes — a real person,
              not a bot.
            </Typography>
            <Button
              variant="contained"
              href={waHref(contact?.whatsapp || FALLBACK_WA)}
              target="_blank"
              sx={{
                bgcolor: '#fff',
                color: 'var(--ink)',
                fontWeight: 700,
                fontSize: 15,
                borderRadius: '100px',
                px: 4,
                py: 1.5,
                textTransform: 'none',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                '&:hover': {
                  bgcolor: '#fff',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
                },
              }}
            >
              💬 Chat on WhatsApp
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default FAQ;
